import { stringify } from 'query-string';
import { set, get, has, cloneDeep } from 'lodash';
import HttpError from './HttpError';
import { i18n } from '../src/i18n';
import { bugsnagNotify } from './bugsnagClientWrapper';
import { getHttpInstance } from './httpInstance';

export const fetchJson = (url, opt = { credentials: 'include' }) => {
  const options = cloneDeep(opt);
  const headersObject = getRequestHeaders(options);
  const user = options?.user;
  // eslint-disable-next-line no-param-reassign
  delete options.user;
  if (options.files) {
    const formData = new FormData();
    formData.append('body', options.body);
    for (const file in options.files) {
      if (file) {
        formData.append(file, options.files[file]);
      }
    }
    delete options.files;
    options.body = formData;
  }
  const httpInstance = getHttpInstance(new URL(url).origin);
  return httpInstance(url, {
    ...options,
    data: options.body,
    headers: headersObject,
  })
    .then(response => ({
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      body: response.data,
    }))
    .catch(error => {
      const errorInfo = {
        url: url || error.url,
        method: error.method,
        statusText: get(error.response, 'statusText', error.errno),
        headers: headersObject,
        status: parseInt(get(error.response, 'status', error.code), 10) || 421,
        body: get(error.response, 'body', null),
        responseText: get(error.response?.data, 'message', ''),
      };

      checkAuthStatus(errorInfo.status);

      const err = HttpError(errorInfo, error);

      if (errorInfo && errorInfo.status >= 400 && errorInfo.status !== 404) {
        bugsnagNotify(err, {
          context: err.message,
          user,
          metadata: errorInfo,
        });
      }

      return Promise.reject(err);
    });
};

export const checkAuthStatus = status => {
  // run only on browser environment
  if (status === 401 && typeof window !== 'undefined') {
    console.error('Forcing logout');
    if (window.Appcues) {
      window.Appcues.reset();
    }

    window.location.href = `/auth/logout?lang=${i18n.language}`;
  }
};

const getRequestHeaders = options => {
  const requestHeaders = options.headers || { Accept: 'application/json; charset=utf-8' };
  if (!has(requestHeaders, 'Content-Type') && options && options.body) {
    if (!options.files) {
      set(requestHeaders, 'Content-Type', 'application/json; charset=utf-8');
    }
  }
  return requestHeaders;
};

export const queryParameters = stringify;

const isValidObject = value => {
  if (!value) {
    return false;
  }

  const isArray = Array.isArray(value);
  const isBuffer = Buffer.isBuffer(value);
  const isObject = Object.prototype.toString.call(value) === '[object Object]';
  const hasKeys = !!Object.keys(value).length;

  return !isArray && !isBuffer && isObject && hasKeys;
};

export const flattenObject = (value, path = []) => {
  if (isValidObject(value)) {
    return Object.assign({}, ...Object.keys(value).map(key => flattenObject(value[key], path.concat([key]))));
  }
  if (Array.isArray(value)) {
    return { [path]: value.join(',') };
  }
  return path.length ? { [path.join('.')]: value } : value;
};
